import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Input, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  ALL_VALUE,
  Avatar,
  CenterSpinner,
  EmptyText,
  ExpandableCard,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  Png,
  RowKV,
  SelectSheet,
  SummaryCard,
  useSheet,
} from "../../../components/managementUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopSearch,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { appImages } from "../../../assets/appicons";
import { resolveImageUrl } from "../../../utils/format";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import {
  EMP_DEPARTMENT,
  JOB_TITLE,
  filterJobPosition,
  useSupportBusinessEndpoint,
} from "../../../utils/managerRole";
import {
  buildOptionSheetData,
  useFilterOptions,
  validId,
  ALL_OPTION,
} from "../../../utils/useFilterOptions";
import {
  getBusinessStaffListApi,
  getSupportBusinessStaffListApi,
} from "../../../api/staffManager.api";
import { EMP_API_BASE_URL, EMP_BASE_PATH } from "../../../config";

const LIMIT = 10;

// Khớp STAFF_* constant của app.
const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ", sortBy: "createdAt", sortOrder: "desc" },
  { key: "OLDEST", label: "Cũ đến mới", sortBy: "createdAt", sortOrder: "asc" },
];
const STATUS_WORKING = 0;
const STATUS_OPTIONS = [
  { key: ALL_VALUE, label: "Tất cả" },
  { key: STATUS_WORKING, label: "Đang làm việc" },
  { key: 1, label: "Đã nghỉ việc" },
];
const DEPARTMENT_OPTIONS = [
  { key: ALL_VALUE, label: "Tất cả" },
  { key: 1, label: "Khối kinh doanh" },
  { key: 0, label: "Khối hỗ trợ kinh doanh" },
  { key: 2, label: "Cộng tác viên" },
];

const StaffCard = ({ item, open, onToggle, onOpenDetail }) => {
  const working = !item?.status;
  return (
    <ExpandableCard
      open={open}
      onToggle={onToggle}
      onDetailClick={onOpenDetail}
      header={
        <Flex align="center" gap="12px">
          <Flex w="44px" h="44px" borderRadius="10px" align="center" justify="center" flexShrink={0}>
            <Avatar src={resolveImageUrl(item?.avatar, EMP_API_BASE_URL)} size={40} />
          </Flex>
          <Box flex="1" minW="0">
            <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
              {item?.fullName || "-"} - {item?.code || "-"}
            </Text>
            <Box h="4px" />
            <Flex align="center" gap="8px">
              <Text fontSize="13px" color={working ? M.green300 : M.red}>
                {working ? "Đang làm việc" : "Dừng làm việc"}
              </Text>
              <Text fontSize="13px" color={M.gray}>
                {item?.startTime ? moment(item.startTime).format("DD/MM/YYYY") : "-"}
              </Text>
            </Flex>
          </Box>
        </Flex>
      }
    >
      <RowKV
        label="Trạng thái chứng chỉ mô giới"
        value={item?.isBrokerDebtCertificate ? "Đã có" : "Chưa có"}
        valueColor={item?.isBrokerDebtCertificate ? M.green300 : M.red}
      />
      <RowKV label="Chi nhánh" value={item?.branch?.name || "-"} />
      {item?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS && (
        <RowKV label="Phòng" value={item?.supportDepartment?.name || "-"} />
      )}
      {item?.department === EMP_DEPARTMENT.BUSINESS && (
        <RowKV label="Khối" value={item?.saleBlock?.name || "-"} />
      )}
      {item?.department === EMP_DEPARTMENT.BUSINESS && (
        <RowKV label="Vùng" value={item?.saleArea?.name || "-"} />
      )}
    </ExpandableCard>
  );
};

const StaffEmployeeList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user } = useEmployeeAuth();
  const fetchApi = useSupportBusinessEndpoint(user)
    ? getSupportBusinessStaffListApi
    : getBusinessStaffListApi;
  const jobPosition = filterJobPosition(user);

  const [keyword, setKeyword] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [status, setStatus] = useState(STATUS_WORKING);
  const [department, setDepartment] = useState(ALL_VALUE);
  const [area, setArea] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [supportDept, setSupportDept] = useState(ALL_OPTION);
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const reqId = useRef(0);
  const searchTimer = useRef(null);

  const onSearchChange = useCallback((e) => {
    const v = e.target.value;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setKeyword(v.trim()), 300);
  }, []);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(
    () => (sheet.type === "BLOCK" ? { saleAreaId: validId(area) } : undefined),
    [sheet.type, area]
  );
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const sortOption = SORT_OPTIONS.find((s) => s.key === sortKey) || SORT_OPTIONS[0];

  const buildParams = useCallback(
    (p) => {
      const params = {
        page: p,
        limit: LIMIT,
        status: status !== ALL_VALUE ? Number(status) : undefined,
        sortBy: sortOption.sortBy,
        sortOrder: sortOption.sortOrder,
        saleAreaId: validId(area),
        saleBlockId: validId(block),
        supportDepartmentId: validId(supportDept),
        search: keyword || undefined,
      };
      // Chỉ TGD (hoặc phòng hỗ trợ đặc biệt) mới lọc theo bộ phận - khớp app.
      if (jobPosition === JOB_TITLE.TGD && department !== ALL_VALUE) {
        if (Number(department) === 2) params.isRelative = true;
        else {
          params.department = Number(department);
          params.isRelative = false;
        }
      }
      return params;
    },
    [status, sortOption, area, block, supportDept, keyword, department, jobPosition]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await fetchApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setTotal(data?.pagination?.total ?? list.length);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [fetchApi, buildParams]
  );

  useEffect(() => {
    setExpanded({});
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, status, department, area?._id, block?._id, supportDept?._id, sortKey]);

  const statusLabel = STATUS_OPTIONS.find((s) => s.key === status)?.label ?? "Tất cả";
  const departmentLabel = DEPARTMENT_OPTIONS.find((s) => s.key === department)?.label ?? "Tất cả";

  // Lưới bộ lọc theo chức vụ - khớp renderFilterGrid của app.
  const filterFields = useMemo(() => {
    const statusField = (
      <FilterField
        key="status"
        label="Trạng thái"
        value={statusLabel}
        onClick={() => sheet.open("STATUS")}
        width={jobPosition === JOB_TITLE.TRUONG_VUNG || jobPosition === JOB_TITLE.TRUONG_PHONG || jobPosition === JOB_TITLE.TGD ? "48%" : "100%"}
      />
    );
    switch (jobPosition) {
      case JOB_TITLE.TRUONG_VUNG:
        return [
          statusField,
          <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
        ];
      case JOB_TITLE.TRUONG_PHONG:
        return [
          statusField,
          <FilterField
            key="sdept"
            label="Phòng ban"
            value={supportDept?.name || "Tất cả"}
            onClick={() => sheet.open("SUPPORT_DEPARTMENT")}
          />,
        ];
      case JOB_TITLE.TGD:
        return [
          statusField,
          <FilterField key="dept" label="Bộ phận" value={departmentLabel} onClick={() => sheet.open("DEPARTMENT")} />,
          <FilterField
            key="sdept"
            label="Phòng ban"
            value={supportDept?.name || "Tất cả"}
            onClick={() => sheet.open("SUPPORT_DEPARTMENT")}
          />,
          <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
          <FilterField key="area" label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />,
        ];
      default:
        return [statusField];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, statusLabel, departmentLabel, block, area, supportDept]);

  const sheetTitle =
    { AREA: "Vùng", BLOCK: "Khối", SUPPORT_DEPARTMENT: "Phòng ban", STATUS: "Trạng thái", DEPARTMENT: "Bộ phận", SORT: "Sắp xếp" }[
      sheet.type
    ] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "STATUS") return STATUS_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "DEPARTMENT") return DEPARTMENT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT") return SORT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "SUPPORT_DEPARTMENT") return buildOptionSheetData(options.items, supportDept);
    return [];
  }, [sheet.type, options.items, area, block, supportDept]);

  const selectedKey = {
    AREA: area?._id,
    BLOCK: block?._id,
    SUPPORT_DEPARTMENT: supportDept?._id,
    STATUS: status,
    DEPARTMENT: department,
    SORT: sortKey,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "STATUS") setStatus(raw);
    if (sheet.type === "DEPARTMENT") setDepartment(raw);
    if (sheet.type === "SORT") setSortKey(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "SUPPORT_DEPARTMENT") setSupportDept(raw);
    sheet.close();
    setSheetSearch("");
  };

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={sheet.type !== "SORT" && sheet.type !== "STATUS" && sheet.type !== "DEPARTMENT"}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={options.loading}
      loadingMore={options.loadingMore}
      hasNextPage={options.hasMore}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => ({
      id: it._id,
      raw: it,
      employee: `${it?.fullName || "-"} - ${it?.code || "-"}`,
      avatar: resolveImageUrl(it?.avatar, EMP_API_BASE_URL),
      working: !it?.status,
      startTime: it?.startTime ? moment(it.startTime).format("DD/MM/YYYY") : "-",
      ccmg: it?.isBrokerDebtCertificate ? "Đã có" : "Chưa có",
      hasCcmg: !!it?.isBrokerDebtCertificate,
      branch: it?.branch?.name || "-",
      unit:
        it?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS
          ? it?.supportDepartment?.name || "-"
          : it?.saleBlock?.name || "-",
      area: it?.department === EMP_DEPARTMENT.BUSINESS ? it?.saleArea?.name || "-" : "-",
    }));

    return (
      <DesktopPage title="Quản lý nhân viên">
        <DesktopStatRow items={[{ key: "total", title: "Số nhân viên", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            <Box w="360px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Tìm kiếm
              </Text>
              <DesktopSearch
                placeholder="Nhập tên nhân viên ..."
                onChange={onSearchChange}
                maxW="360px"
              />
            </Box>
            {filterFields.map((el, i) =>
              React.cloneElement(el, { key: el.key || i, width: "200px" }),
            )}
            <Box w="200px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có nhân viên nào"
            onRowClick={(row) => navigate(`${EMP_BASE_PATH}/quan-ly/nhan-vien/${row.id}`)}
            rows={rows}
            headers={[
              {
                key: "employee",
                label: "Nhân viên",
                align: "left",
                width: "26%",
                render: (row) => (
                  <Flex align="center" gap="12px">
                    <Avatar src={row.avatar} size={36} />
                    <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
                      {row.employee}
                    </Text>
                  </Flex>
                ),
              },
              {
                key: "working",
                label: "Trạng thái",
                width: "13%",
                render: (row) => (
                  <Text fontSize="14px" color={row.working ? M.green300 : M.red}>
                    {row.working ? "Đang làm việc" : "Dừng làm việc"}
                  </Text>
                ),
              },
              { key: "startTime", label: "Ngày vào làm", width: "12%" },
              {
                key: "ccmg",
                label: "CCMG",
                width: "10%",
                render: (row) => (
                  <Text fontSize="14px" color={row.hasCcmg ? M.green300 : M.red}>
                    {row.ccmg}
                  </Text>
                ),
              },
              { key: "branch", label: "Chi nhánh", align: "left", width: "15%" },
              { key: "unit", label: "Khối / Phòng", align: "left", width: "12%" },
              { key: "area", label: "Vùng", align: "left", width: "12%" },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Quản lý nhân viên" />

      <Box px="16px" pt="12px">
        {/* Ô tìm kiếm và "Bộ lọc chi tiết" dính liền thành 1 khối bo góc - khớp app.
            Ô tìm kiếm chỉ có viền trên/trái/phải để không đè lên viền của filter. */}
        <Box>
          <Flex
            h="44px"
            align="center"
            px="12px"
            bg="white"
            border="1px solid"
            borderColor={M.border}
            borderBottom="0"
            borderTopRadius="12px"
          >
            <Png src={appImages.icon_search} size={20} color={M.grey200} />
            <Input
              variant="unstyled"
              ml="8px"
              fontSize="14px"
              fontWeight={600}
              color={M.baseContent}
              placeholder="Nhập tên nhân viên ..."
              _placeholder={{ color: M.grey200, fontWeight: 400 }}
              onChange={onSearchChange}
            />
          </Flex>
          <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)} topRadius={false}>
            {filterFields}
          </FilterPanel>
        </Box>

        <Box h="12px" />
        <SummaryCard text="SỐ NHÂN VIÊN" total={total} />

        <ListHeaderRow title="Danh sách nhân viên" sortLabel={sortOption.label} onSort={() => sheet.open("SORT")} />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText text="Không có nhân viên nào" />
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => (
              <StaffCard
                key={it._id}
                item={it}
                open={!!expanded[it._id]}
                onToggle={() => setExpanded((p) => ({ ...p, [it._id]: !p[it._id] }))}
                onOpenDetail={() => navigate(`${EMP_BASE_PATH}/quan-ly/nhan-vien/${it._id}`)}
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default StaffEmployeeList;
